import { useMemo } from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';

import { useSessionAsync } from 'src/app/shared/hooks/session';
import { isLoaded } from 'src/app/shared/utils/async';

import { useMembershipModalState } from '../../../../modalContext';

type Result = {
	headline: string;
	description: string;
};

function getHeadlineConfig(variant: string | undefined): { key: string; variantSuffix: string } {
	const variantSuffix = ['variant_1', 'variant_2', 'variant_3'].includes(variant ?? '') ? '_variant' : '';
	return { key: 'heading', variantSuffix };
}

function getDescriptionConfig(variant: string | undefined): { key: string; suffix: string } {
	if (variant === 'variant_2' || variant === 'variant_3') {
		return { key: 'subheading', suffix: '_variant' };
	}
	return { key: 'subheading', suffix: '' };
}

export function useHeader(): Result {
	const [{ koiPpmuDesignExperimentVariant, firstName }] = useMembershipModalState();
	const { translate } = useI18n();
	const session = useSessionAsync();

	const { headline, description } = useMemo(() => {
		if (!isLoaded(session)) {
			return { headline: '', description: '' };
		}

		const isGuest = session.value.loginState === 'GUEST';
		const shortDisplayName = session.value.user?.shortDisplayName ?? firstName ?? '';
		const headlineSuffix = isGuest && !firstName ? '_guest' : '';

		const headlineConfig = getHeadlineConfig(koiPpmuDesignExperimentVariant);
		const descriptionConfig = getDescriptionConfig(koiPpmuDesignExperimentVariant);

		return {
			headline: translate(`corgi.co.ppmu.modal.${headlineConfig.key}${headlineConfig.variantSuffix}${headlineSuffix}`, {
				name: shortDisplayName,
			}),
			description: translate(`corgi.co.ppmu.modal.${descriptionConfig.key}${descriptionConfig.suffix}`),
		};
	}, [firstName, koiPpmuDesignExperimentVariant, session, translate]);

	return {
		headline,
		description,
	};
}
